import React, { Component } from "react";
import { withScope, captureReactException } from "@sentry/react";
import AlertMessage from "./UI/Alerts/AlertMessage";
import { showSentryReportDialog } from "./utils/sentry";
import { sentryDsn } from "./utils/env";
import IOButton from "./UI/Form/Button/IOButton";
import TtgFeature from "./UI/TtgFeature";
import Flex from "./UI/Flex/Flex";

/**
 * Root error boundary.
 */
class AppBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { eventId: null };
  }

  static getDerivedStateFromError(error) {
    const isChunkFailedError =
      error?.message && chunkFailedMessage.test(error.message);

    return { hasError: true, error, isChunkFailedError };
  }

  resetErrorBoundary() {
    this.setState({ hasError: false });
  }

  componentDidCatch(error, errorInfo) {
    if (error?.message && chunkFailedMessage.test(error.message)) {
      // The user probably had a tab open before a deploy, and the code-splitting encountered an outdated chunk
      // filename hash. So just reload the page, and it should be fine. Solution #3 from:
      // https://mitchgavan.com/code-splitting-react-safely/

      window.location.reload();
      return;
    }

    withScope((scope) => {
      scope.setExtras(errorInfo);
      const eventId = captureReactException(error, errorInfo, {
        tags: {
          severe: true,
          component_crash: true,
        },
      });
      this.setState({ eventId });
    });
  }

  render() {
    if (this.state.hasError) {
      //render fallback UI
      const toRender = this.props.fallbackRender ? (
        this.props.fallbackRender(this.state.error)
      ) : this.state.isChunkFailedError ? (
        <div>
          <AlertMessage
            message="The web application has been updated. This browser page will automatically refresh."
            status="warning"
          />
        </div>
      ) : (
        <div>
          <AlertMessage
            message="Unfortunately, an unexpected error has occurred. Please contact support."
            status="error"
          />
          <Flex gap="1rem">
            {sentryDsn ? (
              <IOButton
                type="button"
                onClick={() => {
                  showSentryReportDialog({
                    eventId: this.state.eventId,
                  });
                }}
              >
                Report feedback
              </IOButton>
            ) : null}
            {process.env.NODE_ENV !== "production" && (
              <TtgFeature>
                {({ treeIcon }) => (
                  <IOButton
                    type="button"
                    outline
                    onClick={() => {
                      this.resetErrorBoundary();
                    }}
                  >
                    {treeIcon} Reset
                  </IOButton>
                )}
              </TtgFeature>
            )}
          </Flex>
        </div>
      );

      return this.props.wrapFallback
        ? this.props.wrapFallback(toRender)
        : toRender;
    }

    //when there's not an error, render children untouched
    return this.props.children;
  }
}

export default AppBoundary;

const chunkFailedMessage =
  /Loading chunk \d+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Unable to preload CSS for|'text\/html' is not a valid JavaScript MIME type|Importing a module script failed/;
